// =====================================================================
// Les personnes déclarées hors ligne, dans l'arbre
// =====================================================================
// Déclarer quinze personnes au village et n'en voir aucune avant d'avoir
// retrouvé le réseau rendrait la saisie hors ligne inutilisable : on ne
// saurait plus qui on a déjà saisi, et on saisirait deux fois.
//
// On fabrique donc, à partir de la file d'attente, les lignes que le
// serveur renverra plus tard. La génération de chacune est connue
// d'avance — c'est la déclaration elle-même qui la dit :
//
//   parent        dx 1, dy 0
//   grand-parent  dx 2, dy 0
//   frère ou sœur dx 1, dy 1
//   enfant        dx 0, dy 1
//
// Le libellé suit la même règle que la base, et l'identifiant provisoire
// est la clé d'idempotence : elle est unique, et elle disparaît avec
// l'entrée au moment où la vraie fiche arrive.
//
// LES LIENS NE SONT TRACÉS QUE QUAND ON LES CONNAÎT. Un grand-père
// déclaré sans que le parent intermédiaire le soit fera créer au serveur
// un nœud anonyme ; ici on ne peut pas l'inventer, donc le nœud flotte
// dans sa génération jusqu'à l'envoi. Mieux vaut un nœud sans trait qu'un
// trait faux.
// =====================================================================

import type { Lien, Parent, RoleParent, Sexe } from '../partage/types';
import type { EnAttente } from './file-attente';

export interface Attendu extends Parent {
  /** Distingue la ligne provisoire de celles qui viennent du serveur. */
  en_attente: true;
  /** Message du serveur quand l'envoi a été refusé. */
  refus: string | null;
}

const LIBELLE: Record<RoleParent, string> = { pere: 'père', mere: 'mère' };
const LIBELLE_GRAND: Record<RoleParent, string> = {
  pere: 'grand-père',
  mere: 'grand-mère',
};

export function personnesAttendues(file: EnAttente[]): Attendu[] {
  return file.map((e) => {
    const d = e.declaration;
    const base = {
      personne_id: e.cle,
      nom: d.identite.nom ?? null,
      prenoms: d.identite.prenoms ?? null,
      sexe: d.identite.sexe ?? null,
      village_origine: d.identite.village_origine ?? null,
      decede: false,
      en_attente: true as const,
      refus: e.erreur,
    };

    switch (d.genre) {
      case 'parent':
        return {
          ...base,
          libelle: LIBELLE[d.role],
          profondeur_moi: 1,
          profondeur_autre: 0,
          cote: d.role,
        };
      case 'grand-parent':
        return {
          ...base,
          libelle: LIBELLE_GRAND[d.role],
          profondeur_moi: 2,
          profondeur_autre: 0,
          cote: d.cote,
        };
      case 'fratrie':
        return {
          ...base,
          libelle: d.memes_parents
            ? base.sexe === 'F'
              ? 'sœur'
              : 'frère'
            : base.sexe === 'F'
              ? 'demi-sœur'
              : 'demi-frère',
          profondeur_moi: 1,
          profondeur_autre: 1,
          cote: d.memes_parents ? null : (d.parent_partage ?? null),
        };
      case 'enfant':
        return {
          ...base,
          libelle: base.sexe === 'F' ? 'fille' : base.sexe === 'M' ? 'fils' : 'enfant',
          profondeur_moi: 0,
          profondeur_autre: 1,
          cote: null,
        };
    }
  });
}

/**
 * Les traits qu'on peut tracer sans rien inventer, à partir de ce qui est
 * déjà connu : les parents directs venus du serveur, et ceux de la file.
 */
export function liensAttendus(
  file: EnAttente[],
  moi: string,
  sexeMoi: Sexe | null,
  dejaLa: Parent[],
  liens: Lien[],
): Lien[] {
  // Mes parents directs, qu'ils viennent du serveur ou de la file.
  const monParent = new Map<RoleParent, string>();
  for (const l of liens) {
    if (l.enfant_id === moi) monParent.set(l.role_parent, l.parent_id);
  }
  for (const p of dejaLa) {
    if (p.profondeur_moi === 1 && p.profondeur_autre === 0 && p.cote) {
      monParent.set(p.cote, p.personne_id);
    }
  }
  for (const e of file) {
    if (e.declaration.genre === 'parent') {
      monParent.set(e.declaration.role, e.cle);
    }
  }

  const tracés: Lien[] = [];

  for (const e of file) {
    const d = e.declaration;
    switch (d.genre) {
      case 'parent':
        tracés.push({ enfant_id: moi, parent_id: e.cle, role_parent: d.role });
        break;

      case 'grand-parent': {
        // Il faut le parent par lequel on y est rattaché.
        const intermediaire = monParent.get(d.cote);
        if (intermediaire) {
          tracés.push({
            enfant_id: intermediaire,
            parent_id: e.cle,
            role_parent: d.role,
          });
        }
        break;
      }

      case 'fratrie': {
        const roles: RoleParent[] = d.memes_parents
          ? ['pere', 'mere']
          : d.parent_partage
            ? [d.parent_partage]
            : [];
        for (const r of roles) {
          const p = monParent.get(r);
          if (p) tracés.push({ enfant_id: e.cle, parent_id: p, role_parent: r });
        }
        break;
      }

      case 'enfant':
        tracés.push({
          enfant_id: e.cle,
          parent_id: moi,
          role_parent: sexeMoi === 'F' ? 'mere' : 'pere',
        });
        break;
    }
  }

  return tracés;
}
